import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const TINYMCE_SCRIPT_ID = 'seller-tinymce-script-edit';

const SellerProductEdit = () => {
  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [productId, setProductId] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subImages, setSubImages] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [newSubPreviewImages, setNewSubPreviewImages] = useState([]);
  const [editorReady, setEditorReady] = useState(false);

  const [form, setForm] = useState({
    name: '',
    category_id: '',
    short_description: '',
    description: '',
    price: '',
    stock: '',
    sale_price: '',
    sale_start: '',
    sale_end: '',
  });

  const imageRef = useRef(null);
  const subImagesRef = useRef(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const id = Number(params.get('id') || 0);
        setProductId(id);

        const res = await axios.get(
          `/api/seller/product/edit-data?id=${encodeURIComponent(id)}`,
          { withCredentials: true }
        );

        if (res.data && res.data.ok) {
          setShopName(res.data.shop_name || '');
          setCategories(Array.isArray(res.data.categories) ? res.data.categories : []);
          setSubImages(Array.isArray(res.data.sub_images) ? res.data.sub_images : []);

          const nextForm = {
            name: res.data.product?.name || '',
            category_id: res.data.product?.category_id ?? '',
            short_description: res.data.product?.short_description || '',
            description: res.data.product?.description || '',
            price: res.data.product?.price ?? '',
            stock: res.data.product?.stock ?? '',

            sale_price: res.data.product?.sale_price ?? '',
            sale_start: res.data.product?.sale_start
            ? String(res.data.product.sale_start).substring(0,16)
            : '',
            sale_end: res.data.product?.sale_end
              ? String(res.data.product.sale_end).substring(0,16)
              : '',
          };

          setForm(nextForm);

          if (res.data.product?.image_path) {
            setPreviewImage(`/static/${String(res.data.product.image_path).replace(/^\/+/, '')}`);
          } else {
            setPreviewImage('');
          }
        } else {
          alert(res.data?.error || '상품 정보를 불러오지 못했습니다.');
        }
      } catch (err) {
        const redirect = err?.response?.data?.redirect;
        const error = err?.response?.data?.error;

        if (redirect) {
          window.location.href = redirect;
          return;
        }

        alert(error || '상품 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!descriptionRef.current) return;

    const initEditor = () => {
      if (!window.tinymce) return;

      const existing = window.tinymce.get('description');
      if (existing) {
        existing.remove();
      }

      window.tinymce.init({
        selector: '#description',
        height: 400,
        plugins: 'image link media table code lists',
        toolbar:
          'undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist | image media link | code',
        language: 'ko_KR',
        images_upload_url: '/api/seller/upload/detail-image',
        images_upload_credentials: true,
        automatic_uploads: true,
        images_reuse_filename: true,
        relative_urls: true,
        remove_script_host: true,
        document_base_url: '/',
        setup: (editor) => {
          editor.on('init', () => {
            setEditorReady(true);
            editor.setContent(form.description || '');
          });

          editor.on('change keyup undo redo', () => {
            const content = editor.getContent();
            setForm((prev) => ({
              ...prev,
              description: content,
            }));
          });
        },
      });
    };

    if (window.tinymce) {
      initEditor();
      return () => {
        const editor = window.tinymce?.get('description');
        if (editor) editor.remove();
      };
    }

    let script = document.getElementById(TINYMCE_SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = TINYMCE_SCRIPT_ID;
      script.src = '/static/flask_js/tinymce/tinymce.min.js';
      script.async = true;
      script.onload = initEditor;
      document.body.appendChild(script);
    } else {
      if (window.tinymce) {
        initEditor();
      } else {
        script.onload = initEditor;
      }
    }

    return () => {
      const editor = window.tinymce?.get('description');
      if (editor) editor.remove();
    };
  }, [loading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreviewImage(ev.target?.result || '');
    };
    reader.readAsDataURL(file);
  };

  const handleSubImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    const previews = [];

    if (files.length === 0) {
      setNewSubPreviewImages([]);
      return;
    }

    let done = 0;
    files.forEach((file, idx) => {
      if (!file.type.startsWith('image/')) {
        done += 1;
        if (done === files.length) {
          setNewSubPreviewImages(previews.filter(Boolean));
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        previews[idx] = ev.target?.result || '';
        done += 1;
        if (done === files.length) {
          setNewSubPreviewImages(previews.filter(Boolean));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const resizeImage = (file, maxWidth, callback) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      const img = new Image();
      img.onload = function () {
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          const scale = maxWidth / width;
          width = maxWidth;
          height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          function (blob) {
            callback(blob || file);
          },
          'image/jpeg',
          0.9
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteSubImage = async (imageId) => {
    const ok = window.confirm('이 이미지를 삭제하시겠습니까?');
    if (!ok) return;

    try {
      const res = await axios.post(
        '/api/seller/product/image/delete-process',
        {
          id: imageId,
          product_id: productId,
        },
        { withCredentials: true }
      );

      if (res.data && res.data.ok) {
        setSubImages((prev) => prev.filter((img) => img.id !== imageId));
        return;
      }

      alert(res.data?.error || '이미지 삭제 실패');
    } catch (err) {
      alert(err?.response?.data?.error || '이미지 삭제 실패');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (window.tinymce) {
      window.tinymce.triggerSave();
      const editor = window.tinymce.get('description');
      if (editor) {
        form.description = editor.getContent();
      }
    }

    const sendForm = async (blob = null) => {
      const formData = new FormData();

      formData.append('name', form.name);
      formData.append('category_id', form.category_id);
      formData.append('short_description', form.short_description);
      formData.append('description', form.description);
      formData.append('price', form.price);
      formData.append('stock', form.stock);
      formData.append('sale_price', form.sale_price);
      formData.append('sale_start', form.sale_start);
      formData.append('sale_end', form.sale_end);

      if (blob) {
        formData.set('image', blob, `product_${Date.now()}.jpg`);
      } else if (imageRef.current?.files?.[0]) {
        formData.set('image', imageRef.current.files[0]);
      }

      const subFiles = Array.from(subImagesRef.current?.files || []);
      subFiles.forEach((file) => {
        formData.append('sub_images[]', file);
      });

      try {
        const res = await fetch(`/api/seller/product/edit-process?id=${productId}`, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
        });

        const text = await res.text();
        const trimmed = (text || '').trim();

        if (
          trimmed.startsWith('/seller/product/list') ||
          trimmed.endsWith('.php') ||
          trimmed.startsWith('/')
        ) {
          window.location.href = trimmed;
        } else {
          alert(trimmed || '수정 중 오류가 발생했습니다.');
        }
      } catch (err) {
        alert('업로드 실패');
      }
    };

    const file = imageRef.current?.files?.[0];
    if (file) {
      resizeImage(file, 1200, sendForm);
    } else {
      sendForm();
    }
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.loadingWrap}>로딩중...</div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <h2 style={styles.title}>상품 수정</h2>

      <form onSubmit={handleSubmit} style={styles.formCard}>
        <input type="hidden" name="product_id" value={productId} />

        <div style={styles.mb3}>
          <label style={styles.label}>상품명</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>카테고리</label>
          <select
            name="category_id"
            value={form.category_id}
            onChange={handleChange}
            style={styles.select}
          >
            <option value="">선택하세요</option>
            {categories.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>간단설명</label>
          <textarea
            name="short_description"
            rows="1"
            value={form.short_description}
            onChange={handleChange}
            style={styles.textareaShort}
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>상세설명</label>
          <textarea
            id="description"
            ref={descriptionRef}
            name="description"
            rows="10"
            defaultValue={form.description}
            style={styles.textarea}
          />
          {!editorReady ? (
            <div style={styles.editorLoading}>에디터 로딩중...</div>
          ) : null}
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>가격(원)</label>
          <input
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>할인가</label>
          <input
            type="number"
            name="sale_price"
            value={form.sale_price}
            onChange={handleChange}
            style={styles.input}
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>할인 시작일</label>
          <input
            type="datetime-local"
            name="sale_start"
            value={form.sale_start}
            onChange={handleChange}
            style={styles.input}
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>할인 종료일</label>
          <input
            type="datetime-local"
            name="sale_end"
            value={form.sale_end}
            onChange={handleChange}
            style={styles.input}
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>재고 수량</label>
          <input
            type="number"
            name="stock"
            value={form.stock}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>대표 이미지</label>
          {previewImage ? (
            <img src={previewImage} alt="" style={styles.mainPreview} />
          ) : null}
          <input
            type="file"
            ref={imageRef}
            name="image"
            onChange={handleMainImageChange}
            style={styles.input}
            accept="image/*"
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>추가 이미지 (여러 개 선택 가능)</label>

          <div style={styles.previewRow}>
            {newSubPreviewImages.map((src, idx) => (
              <img key={idx} src={src} alt="" style={styles.newSubPreview} />
            ))}
          </div>

          <input
            type="file"
            ref={subImagesRef}
            name="sub_images[]"
            multiple
            onChange={handleSubImagesChange}
            style={styles.input}
            accept="image/*"
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>기존 추가 이미지</label>
          <div style={styles.oldSubWrap}>
            {subImages.length > 0 ? (
              subImages.map((subImg) => (
                <div key={subImg.id} style={styles.subCard}>
                  <img
                    src={`/static/${String(subImg.image_path).replace(/^\/+/, '')}`}
                    alt=""
                    style={styles.oldSubImage}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteSubImage(subImg.id)}
                    style={styles.subDeleteButton}
                  >
                    삭제
                  </button>
                </div>
              ))
            ) : (
              <div style={styles.emptyText}>등록된 추가 이미지가 없습니다.</div>
            )}
          </div>
        </div>

        <div style={styles.formFooter}>
          <button type="submit" style={styles.submitButton}>
            상품 수정
          </button>
        </div>
      </form>
    </SellerLayout>
  );
};

const styles = {
  loadingWrap: {
    padding: '40px 0',
  },
  title: {
    marginTop: '20px',
    marginBottom: '30px',
    fontWeight: 'bold',
    borderBottom: '2px solid #dee2e6',
    paddingBottom: '10px',
  },
  formCard: {
    padding: '24px',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #dee2e6',
  },
  mb3: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
    background: '#fff',
  },
  textareaShort: {
    width: '100%',
    minHeight: '44px',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  textarea: {
    width: '100%',
    minHeight: '220px',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  editorLoading: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#666',
  },
  mainPreview: {
    maxWidth: '200px',
    display: 'block',
    marginBottom: '10px',
  },
  previewRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '10px',
  },
  newSubPreview: {
    maxWidth: '100px',
    display: 'block',
    border: '1px solid #ddd',
    padding: '4px',
  },
  oldSubWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  subCard: {
    border: '1px solid #ddd',
    padding: '8px',
    textAlign: 'center',
  },
  oldSubImage: {
    maxWidth: '100px',
    display: 'block',
    marginBottom: '8px',
  },
  subDeleteButton: {
    border: '1px solid #dc3545',
    background: '#fff',
    color: '#dc3545',
    padding: '6px 10px',
    cursor: 'pointer',
    borderRadius: '4px',
    fontSize: '13px',
  },
  emptyText: {
    color: '#6c757d',
  },
  formFooter: {
    textAlign: 'right',
  },
  submitButton: {
    background: '#0d6efd',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 16px',
    cursor: 'pointer',
  },
};

export default SellerProductEdit;